import React from 'react';
import { View, StyleSheet, ActivityIndicator } from 'react-native';
import { Text } from '@/components/common/text';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { GlassCard } from '../GlassCard';
import { WifiConnectionStatus } from '@/types/residents/wifi';
import { strings } from '@/constants/residents/strings';

interface WifiConnectionRowProps {
  status: WifiConnectionStatus;
  onPress: () => void;
  isLoading?: boolean;
}

export const WifiConnectionRow: React.FC<WifiConnectionRowProps> = ({
  status,
  onPress,
  isLoading = false,
}) => {
  const { colors } = useThemeColors();

  const isConnected = status === WifiConnectionStatus.CONNECTED;
  const isConnecting = status === WifiConnectionStatus.CONNECTING;
  const showSpinner = isLoading || isConnecting;

  const getTitle = () => {
    if (isConnected) return strings.wifi.button.connected;
    if (isConnecting) return 'Connecting...';
    return 'Open WiFi Settings';
  };

  const titleColor = isConnected ? PRIMITIVE_COLORS.successGreen : colors.text.primary;

  return (
    <GlassCard
      interactive
      onPress={onPress}
      borderRadius={12}
      padding={12}
      style={styles.card}
    >
      <View style={styles.row}>
        {showSpinner ? (
          <ActivityIndicator size="small" color={colors.text.primary} />
        ) : (
          <RESIDENTS_SVG_COMPONENTS.WIFI width={24} height={24} color={titleColor} />
        )}
        <View style={styles.textContainer}>
          <Text style={[styles.title, { color: titleColor }]}>{getTitle()}</Text>
          <Text style={[styles.subtitle, { color: colors.text.muted }]}>
            Tap to connect on your device
          </Text>
        </View>
      </View>
    </GlassCard>
  );
};

const styles = StyleSheet.create({
  card: {
    marginBottom: 16,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  textContainer: {
    flex: 1,
    gap: 2,
  },
  title: {
    ...REDESIGN_TYPOGRAPHY.small,
    fontWeight: '600',
  },
  subtitle: {
    ...REDESIGN_TYPOGRAPHY.caption,
  },
});
